'use client';

import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import type { UIMessage } from 'ai';

interface Session {
  id: string;
  title: string;
  category?: string;
  vizType?: string;
  keywords?: string[];
  metadataStatus?: 'none' | 'pending' | 'completed' | 'failed';
  activeWorkflowRunId?: string | null;
  activeWorkflowStartedAt?: string | null;
  /** Projects feature — null/undefined means "ungrouped". */
  projectId?: string | null;
  /** Conversation branching — set when this session was forked from another. */
  parentSessionId?: string | null;
  branchedFromMessageId?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Projects feature — a named folder that groups chat sessions. */
interface Project {
  id: string;
  userId: string;
  name: string;
  icon?: string | null;
  sessionCount: number;
  createdAt: string;
  updatedAt: string;
}

interface SessionContextType {
  currentSessionId: string | null;
  setCurrentSessionId: (id: string | null) => void;
  sessions: Session[];
  setSessions: (sessions: Session[]) => void;
  refreshSessions: () => Promise<void>;
  loadSession: (sessionId: string) => Promise<{ messages: UIMessage[]; activeWorkflowRunId: string | null; bookmarkedTurns: number[] } | null>;
  saveMessages: (sessionId: string, messages: UIMessage[]) => Promise<void>;
  createSession: (title?: string) => Promise<string | null>;
  pollSessionMetadata: (sessionId: string) => void;
  pollLatestAssistantMetadata: (sessionId: string, onFound: (metadata: Record<string, unknown>) => void) => void;
  // Projects feature
  projects: Project[];
  setProjects: (projects: Project[]) => void;
  refreshProjects: () => Promise<void>;
  createProject: (name: string, icon?: string) => Promise<Project | null>;
  renameProject: (projectId: string, updates: { name?: string; icon?: string | null }) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  assignSessionToProject: (sessionId: string, projectId: string | null) => Promise<boolean>;
}

const SessionContext = createContext<SessionContextType | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const activeMetadataPollsRef = useRef<Map<string, AbortController>>(new Map());
  const activeMessageMetadataPollsRef = useRef<Map<string, AbortController>>(new Map());

  const refreshSessions = useCallback(async () => {
    try {
      const res = await fetch('/api/sessions');
      if (res.ok) {
        const data = await res.json();
        setSessions(data);
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    }
  }, []);

  const refreshProjects = useCallback(async () => {
    try {
      const res = await fetch('/api/projects');
      if (res.ok) {
        const data = await res.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const createProject = useCallback(async (name: string, icon?: string): Promise<Project | null> => {
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, icon }),
      });
      if (res.ok) {
        const project = await res.json();
        setProjects(prev => [project, ...prev]);
        return project;
      }
    } catch (error) {
      console.error('Failed to create project:', error);
    }
    return null;
  }, []);

  const renameProject = useCallback(async (projectId: string, updates: { name?: string; icon?: string | null }) => {
    try {
      const res = await fetch(`/api/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const updated = await res.json();
        setProjects(prev => prev.map(p => (p.id === projectId ? { ...p, ...updated } : p)));
      }
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  }, []);

  const deleteProject = useCallback(async (projectId: string) => {
    try {
      const res = await fetch(`/api/projects/${projectId}`, { method: 'DELETE' });
      if (res.ok) {
        setProjects(prev => prev.filter(p => p.id !== projectId));
        // Sessions in this project detach server-side (FK ON DELETE SET NULL) —
        // mirror that locally so the sidebar doesn't show a stale project filter.
        setSessions(prev => prev.map(s => (s.projectId === projectId ? { ...s, projectId: null } : s)));
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
    }
  }, []);

  const assignSessionToProject = useCallback(async (sessionId: string, projectId: string | null): Promise<boolean> => {
    try {
      const res = await fetch(`/api/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId }),
      });
      if (res.ok) {
        setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, projectId } : s)));
        // Session counts shown per-project can go stale otherwise.
        refreshProjects();
        return true;
      }
    } catch (error) {
      console.error('Failed to assign session to project:', error);
    }
    return false;
  }, [refreshProjects]);

  // Poll a single session for metadata updates
  const pollSessionMetadata = useCallback((sessionId: string) => {
    const activePolls = activeMetadataPollsRef.current;
    const existing = activePolls.get(sessionId);
    if (existing) {
      existing.abort();
      activePolls.delete(sessionId);
    }

    const controller = new AbortController();
    activePolls.set(sessionId, controller);
    let attempts = 0;
    const maxAttempts = 10;
    const poll = async () => {
      if (controller.signal.aborted) return;
      attempts++;
      try {
        const res = await fetch(`/api/sessions/${sessionId}`, {
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          const session = data.session as Session;
          
          if (session.metadataStatus === 'completed' || session.metadataStatus === 'failed' || attempts >= maxAttempts) {
            // Update local session list with new metadata
            setSessions(prev => prev.map(s => 
              s.id === sessionId ? { ...s, ...session } : s
            ));
            activePolls.delete(sessionId);
            return;
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to poll session:', error);
        }
        activePolls.delete(sessionId);
        return;
      }
      if (!controller.signal.aborted) {
        window.setTimeout(poll, 2000);
      }
    };
    window.setTimeout(poll, 2000);
  }, []);

  // Poll for the follow-up suggestion chips that generate AFTER a turn's
  // durable finalize completes (fire-and-forget on the server — see
  // apps/server/src/chat/finalize.ts — so they are never present at the
  // moment `status` flips to 'ready'). Short, bounded poll: if the metadata
  // hasn't landed within ~6 attempts we give up silently — a page reload
  // would still pick it up since it's persisted on the message row.
  const pollLatestAssistantMetadata = useCallback((sessionId: string, onFound: (metadata: Record<string, unknown>) => void) => {
    const activePolls = activeMessageMetadataPollsRef.current;
    const existing = activePolls.get(sessionId);
    if (existing) {
      existing.abort();
      activePolls.delete(sessionId);
    }

    const controller = new AbortController();
    activePolls.set(sessionId, controller);
    let attempts = 0;
    const maxAttempts = 6;
    const poll = async () => {
      if (controller.signal.aborted) return;
      attempts++;
      try {
        const res = await fetch(`/api/sessions/${sessionId}`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          const sessionMessages = (data.messages ?? []) as Array<{ role: string; metadata?: Record<string, unknown> | null }>;
          const lastAssistant = [...sessionMessages].reverse().find((m) => m.role === 'assistant');
          const suggestions = lastAssistant?.metadata?.followUpSuggestions;
          if (Array.isArray(suggestions) && suggestions.length > 0) {
            onFound(lastAssistant!.metadata as Record<string, unknown>);
            activePolls.delete(sessionId);
            return;
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Failed to poll message metadata:', error);
        }
        activePolls.delete(sessionId);
        return;
      }
      if (attempts >= maxAttempts) {
        activePolls.delete(sessionId);
        return;
      }
      if (!controller.signal.aborted) {
        window.setTimeout(poll, 1500);
      }
    };
    window.setTimeout(poll, 1500);
  }, []);

  const loadSession = useCallback(async (sessionId: string): Promise<{ messages: UIMessage[]; activeWorkflowRunId: string | null; bookmarkedTurns: number[] } | null> => {
    try {
      const res = await fetch(`/api/sessions/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        return {
          messages: data.messages.map((msg: { id: string; role: string; parts: unknown[]; metadata?: unknown }) => ({
            id: msg.id,
            role: msg.role,
            parts: msg.parts,
            metadata: msg.metadata,
          })),
          activeWorkflowRunId: data.session?.activeWorkflowRunId ?? null,
          // History-panel bookmarks (turn ordinals) — see lib/session-bookmarks.
          bookmarkedTurns: Array.isArray(data.session?.bookmarkedTurns) ? data.session.bookmarkedTurns : [],
        };
      }
    } catch (error) {
      console.error('Failed to load session:', error);
    }
    return null;
  }, []);

  const saveMessages = useCallback(async (sessionId: string, messages: UIMessage[]) => {
    // This will be called by the chat to persist messages
    // For now, we save after each message pair (user + assistant)
    try {
      await fetch(`/api/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages }),
      });
    } catch (error) {
      console.error('Failed to save messages:', error);
    }
  }, []);

  const createSession = useCallback(async (title?: string): Promise<string | null> => {
    try {
      const res = await fetch('/api/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title || 'New Chat' }),
      });
      if (res.ok) {
        const session = await res.json();
        setSessions(prev => [session, ...prev]);
        return session.id;
      }
    } catch (error) {
      console.error('Failed to create session:', error);
    }
    return null;
  }, []);

  return (
    <SessionContext.Provider
      value={{
        currentSessionId,
        setCurrentSessionId,
        sessions,
        setSessions,
        refreshSessions,
        loadSession,
        saveMessages,
        createSession,
        pollSessionMetadata,
        pollLatestAssistantMetadata,
        projects,
        setProjects,
        refreshProjects,
        createProject,
        renameProject,
        deleteProject,
        assignSessionToProject,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}
